import {Image, Platform} from 'react-native';
import {makeAutoObservable, runInAction} from 'mobx';

import config from '../config';
import GiftCardsIconImg from '../images/brand/icons/category/gift-cards.png';
import SpecialsIconImg from '../images/brand/icons/category/specials.png';
import {Department} from '../models/middleware';
import apiService, {GetDepartmentLinesRequestParams} from '../transport/apiService';
import {OrderLine} from '../transport/apiServiceResponse';
import {logger, serializeError} from '../utils/logger';

import type {RootStore} from './rootStore';

interface DepartmentLinesMeta {
  page: number;
  page_count: number;
  total_count?: number;
}

interface DepartmentLinesResponse {
  data: OrderLine[];
  meta: DepartmentLinesMeta;
}

interface SortOption {
  id: string;
  name: string;
}

interface FilterOption {
  query_value: string;
  [key: string]: unknown;
}

const specialsIconUri =
  Platform.OS === 'web' ? SpecialsIconImg : Image.resolveAssetSource(SpecialsIconImg).uri;

const giftCardsIconUri =
  Platform.OS === 'web' ? GiftCardsIconImg : Image.resolveAssetSource(GiftCardsIconImg).uri;

export const SPECIALS_TILE = 'SPECIALS_TILE';
export const GIFT_CARDS_TILE = 'GIFT_CARDS_TILE';

export type DepartmentsLoadState = 'idle' | 'loaded' | 'failed';

export class RangeStore {
  rootStore: RootStore;
  // Department selection
  parentDepartment: Department | null = null;
  departments: Department[] = [];
  departmentsLoadState: DepartmentsLoadState = 'idle';
  // Id of the store whose departments are in `departments`; null until a load succeeds.
  departmentsStoreId: string | null = null;
  // Id of the store of the latest load attempt, written on success and on failure (R64), so
  // `departmentsLoadState` can be read against the selected store.
  departmentsLoadStoreId: string | null = null;
  private departmentsLoadSeq = 0;
  selectedDepartment: Department | null = null;
  selectedSubDepartment: Department | null = null;
  // Range product list
  departmentLines: OrderLine[] = [];
  departmentLinesMeta: DepartmentLinesMeta = {page: 0, page_count: 0};
  // Filter & Sort options
  selectedSortOption: SortOption | null = null;
  selectedFilterOptions: FilterOption[] | null = null;
  // Loading flags
  isLoadingDepartmentLines = false;
  isLoadingNextPage = false;

  // This is a specials tile
  specialsDepartment: Department = {
    id: SPECIALS_TILE,
    type: 'departments',
    name: 'Specials',
    parent: null,
    position: -5,
    promote_at_checkout: false,
    links: {
      icon: specialsIconUri,
    },
  };

  giftCardsDepartment: Department = {
    id: GIFT_CARDS_TILE,
    type: 'departments',
    name: 'Gift Cards',
    parent: null,
    position: -4,
    promote_at_checkout: false,
    links: {
      icon: giftCardsIconUri,
    },
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  reset = () => {
    this.parentDepartment = null;
    this.departments = [];
    this.departmentsLoadState = 'idle';
    this.departmentsStoreId = null;
    this.departmentsLoadStoreId = null;
    this.selectedDepartment = null;
    this.departmentLines = [];
    this.departmentLinesMeta = {page: 0, page_count: 0};
    this.selectedSubDepartment = null;
    this.selectedSortOption = null;
    this.selectedFilterOptions = null;
  };

  get sortedAndFilteredDepartments() {
    return this.departments
      .slice()
      .sort((a: Department, b: Department) => a.position - b.position)
      .filter((item: Department) => item.parent?.id === this.parentDepartment?.id);
  }

  // Keep filterDepartmentsByParent for potential future use with parameters
  filterDepartmentsByParent = (parentId?: string) =>
    this.departments
      .slice()
      .sort((a: Department, b: Department) => a.position - b.position)
      .filter((item: Department) => item.parent?.id === (parentId || this.parentDepartment?.id));

  loadDepartments = async () => {
    const seq = ++this.departmentsLoadSeq;
    const storeId = this.rootStore.shopSelectionStore?.selectedStore?.id ?? null;
    try {
      const departments = await apiService.getDepartments();
      const parentDept = departments.filter((item: Department) => item.parent === null)[0];
      const specials = {
        ...this.specialsDepartment,
        parent: {
          id: parentDept.id,
        },
      };
      const giftCards = {
        ...this.giftCardsDepartment,
        parent: {
          id: parentDept.id,
        },
      };
      const finalDepartments = [...departments, specials];
      if (config.FF_IS_GIFT_CARDS_ENABLED) {
        finalDepartments.push(giftCards);
      }
      runInAction(() => {
        // A response that is not the latest call writes nothing.
        if (seq !== this.departmentsLoadSeq) {
          return;
        }
        this.parentDepartment = parentDept;
        // @ts-expect-error - Type mismatch needs investigation
        this.departments = finalDepartments;
        this.departmentsStoreId = storeId;
        this.departmentsLoadStoreId = storeId;
        this.departmentsLoadState = 'loaded';
      });
    } catch (err) {
      runInAction(() => {
        if (seq === this.departmentsLoadSeq) {
          this.departmentsLoadStoreId = storeId;
          this.departmentsLoadState = 'failed';
        }
      });
      logger.error('RangeStore', 'Error loading departments', serializeError(err));
    }
  };

  selectDepartment = (department: Department) => {
    runInAction(() => {
      this.selectedDepartment = department;
    });
    if (department.id === SPECIALS_TILE) {
      apiService.setDepartment({specials: 1});
    } else {
      apiService.setDepartment({department_id: department.id});
    }
  };

  selectSubDepartment = (department: Department) => {
    runInAction(() => {
      this.selectedSubDepartment = department;
    });
    if (department.id === SPECIALS_TILE) {
      apiService.setDepartment({specials: 1});
    } else {
      apiService.setDepartment({department_id: department.id});
    }
  };

  getSubDepartments = (departmentId?: string) =>
    this.filterDepartmentsByParent(departmentId || this.selectedDepartment?.id);

  loadRangeLines = async () => {
    runInAction(() => {
      this.isLoadingDepartmentLines = true;
      this.departmentLines = [];
    });
    // sort options
    const options: GetDepartmentLinesRequestParams = {};
    if (this.selectedSortOption) {
      options.sort_option_id = this.selectedSortOption.id;
    }
    // filter options
    if (this.selectedFilterOptions) {
      options.facets = this.selectedFilterOptions;
    }

    try {
      const data = (await apiService.getDepartmentLines(options)) as DepartmentLinesResponse;
      runInAction(() => {
        this.departmentLines = data.data;
        this.departmentLinesMeta = data.meta;
      });
    } catch (err) {
      logger.error('RangeStore', 'Error loading lines', serializeError(err));
    }

    runInAction(() => {
      this.isLoadingDepartmentLines = false;
    });
  };

  loadNextPageRangeLines = () => {
    if (this.isLoadingNextPage) {
      return;
    }
    if (this.departmentLinesMeta.page < this.departmentLinesMeta.page_count) {
      this.isLoadingNextPage = true;
      const options: GetDepartmentLinesRequestParams = {};
      if (this.selectedSortOption) {
        options.sort_option_id = this.selectedSortOption.id;
      }
      if (this.selectedFilterOptions) {
        options.facets = this.selectedFilterOptions;
      }
      options.page = this.departmentLinesMeta.page + 1;
      apiService
        .getDepartmentLines(options)
        .then((data: unknown) => {
          const response = data as DepartmentLinesResponse;
          runInAction(() => {
            this.departmentLines = [...this.departmentLines, ...response.data];
            this.departmentLinesMeta = response.meta;
            this.isLoadingDepartmentLines = false;
            this.isLoadingNextPage = false;
          });
        })
        .catch(err => {
          runInAction(() => {
            this.isLoadingDepartmentLines = false;
            this.isLoadingNextPage = false;
          });
          logger.error('RangeStore', 'Error loading next page', serializeError(err));
        });
    }
  };

  setSelectedSortOption = (option: SortOption | null) => (this.selectedSortOption = option);

  setSelectedFilterOptions = (filters: FilterOption[] | null) =>
    (this.selectedFilterOptions = filters);

  clearSelectedFilterOptions = () => (this.selectedFilterOptions = null);

  clearSelectedSortAndFilter = (defaultSortOption: SortOption | null) =>
    runInAction(() => {
      this.selectedSortOption = defaultSortOption;
      this.selectedFilterOptions = null;
    });
}
